/**
 * Shared helpers for the `/api/geo` edge endpoint.
 *
 * `/api/geo` returns the visitor's country (from CDN headers) and the
 * Sec-GPC signal. These values do not change mid-session, so we cache
 * the response in `sessionStorage` to avoid re-fetching on every page load.
 */

export interface GeoResponse {
  country: string | null
  gpc: boolean
}

const GEO_CACHE_KEY = 'purebox.geo.v1';

export function readGeoCache(): GeoResponse | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GeoResponse;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return {
      country: typeof parsed.country === 'string' ? parsed.country : null,
      gpc: Boolean(parsed.gpc),
    };
  } catch {
    return null;
  }
}

export function writeGeoCache(value: GeoResponse): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(value));
  } catch {
    // storage unavailable / quota - best-effort only
  }
}

/**
 * Fetch geo data, reusing the session-scoped cache when available.
 * Returns `null` if the endpoint is unreachable (caller decides fallback).
 */
export async function fetchGeo(): Promise<GeoResponse | null> {
  const cached = readGeoCache();
  if (cached) return cached;

  try {
    const res = await fetch('/api/geo', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<GeoResponse>;
    const value: GeoResponse = {
      country: typeof data?.country === 'string' ? data.country : null,
      gpc: Boolean(data?.gpc),
    };
    writeGeoCache(value);
    return value;
  } catch {
    return null;
  }
}
